import React from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Collapse,
  Container,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Tag,
  TagLabel,
  VStack,
  Text,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Radio,
  RadioGroup,
} from "@chakra-ui/react";
import { DeleteIcon, AddIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { FaCheckCircle } from "react-icons/fa";
import { TbCalculatorOff, TbCalculator } from "react-icons/tb";
import { ColumnInfo, CorrelationProto, FilterProto, Header, MetricEdgeProto, ModelInfo, ObservationColumnProto } from "build-ts/metadata";
import { AggregationType, aggregationTypeToJSON, ColumnType, FilterOperator, filterOperatorToJSON, SortByEnum, sortByEnumToJSON } from "build-ts/enums";
import { CreateOrUpdateMetrics_Request } from "build-ts/interface";
import ObservationComponent from "../../../containers/configuration/ObservationContainer";
import { DateFilter } from "build-ts/relative_date";
import { DateFilterCategory } from "utils/enum";
import { getDateFilterName } from "utils/nameUtils";

interface CreateMKG1Props {
  metricInfo: CreateOrUpdateMetrics_Request | null;
  modelInfo: ModelInfo | null;
  targetModelInfo?: ModelInfo | null; 
  measureColumn: Header[] | null;
  attributeColumn: Header[] | null;
  allMetrics: Header[] | null;
  allBenchmarks: Header[] | null;
  allKGColumns: Header[] | null;
  selectedBenchmark: Header | null;
  handleAddSelectedBenchmark: (id: string) => void;
  relatedAttributeEdge: MetricEdgeProto[] | null;
  selectedRelatedAttributeColumn: Header | null;
  setSelectedRelatedAttributeColumn: React.Dispatch<
    React.SetStateAction<Header | null>
  >;
  relatedAttributeEdgeWeight: number;
  setRelatedAttributeEdgeWeight: React.Dispatch<React.SetStateAction<number>>;
  benchmarkEdge: MetricEdgeProto[];
  benchmarkFilter: FilterProto[] | null;
  benchmarkName: string;
  setBenchmarkName: React.Dispatch<React.SetStateAction<string>>;
  benchmarkFilterValue: string;
  setBenchmarkFilterValue: React.Dispatch<React.SetStateAction<string>>;
  selectedBenchmarkFilterColumn: ColumnInfo | null;
  setSelectedBenchmarkFilterColumn: React.Dispatch<
    React.SetStateAction<ColumnInfo | null>
  >;
  benchmarkEdgeWeight: number;
  setBenchmarkEdgeWeight: React.Dispatch<React.SetStateAction<number>>;
  fundamentalEdgeName: string;
  setFundamentalEdgeName: React.Dispatch<React.SetStateAction<string>>;
  fundamentalEdgeWeight: number;
  setFundamentalEdgeWeight: React.Dispatch<React.SetStateAction<number>>;
  selectedType: string;
  setSelectedType: React.Dispatch<React.SetStateAction<string>>;
  setSelectedHeader: React.Dispatch<React.SetStateAction<string>>;
  correlationFactor: string | "";
  setCorrelationFactor: React.Dispatch<React.SetStateAction<string | "">>;
  correlations: CorrelationProto[];
  fundamentalEdges: MetricEdgeProto[];
  handleAddRelatedAttributeEdge: () => void;
  handleRemoveRelatedAttribute: (id: string) => void;
  handleAddBenchmarkFilter: () => void;
  handleAddBenchmark: () => void;
  handleRemoveBenchmarkFilter: (id: string) => void;
  handleRemoveBenchmarkEdge: (index: number) => void;
  handleAddCorrelation: () => void;
  handleRemoveCorrelation: (index: number) => void;
  handleAddFundamentalEdges: () => void;
  handleRemoveFundamentalEdges: (index: number) => void;
  submitForm: () => void;
  handleTargetModelSelect: (id: string) => void;
  isAdditive: boolean;
  setIsAdditive: React.Dispatch<React.SetStateAction<boolean>>
  isFilterMenuOpen: boolean;
  isCorrelationMenuOpen: boolean;
  toggleCorrelationMenu: () => void;
  toggleFilterMenu: () => void;
  columnNames: string[];
  selectedColumnValues: string[];
  handleToggle: (value: string) => void;

  selectedObservationColumn: Header | null;
  selectedFilterColumn: ColumnInfo | null;
  selectedAggregation?: AggregationType;
  selectedOperator?: FilterOperator;
  selectedSorting?: SortByEnum;
  inputValue: string;
  selectedFilterColumns: FilterProto[];
  selectedObservationColumns: ObservationColumnProto[];
  targetDataModelHeaders: Header[];
  selectedTargetModelId: string;
  benchmarkMode: "currentModel" | "targetModel";
  isLoading: boolean;

  setSelectedColumnValues: React.Dispatch<React.SetStateAction<string[]>>;
  setColumnNames: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedObservationColumn: React.Dispatch<React.SetStateAction<Header | null>>;
  setSelectedFilterColumn: React.Dispatch<React.SetStateAction<ColumnInfo | null>>;
  setSelectedAggregation: React.Dispatch<React.SetStateAction<AggregationType | undefined>>;
  setSelectedOperator: React.Dispatch<React.SetStateAction<FilterOperator | undefined>>;
  setSelectedSorting: React.Dispatch<React.SetStateAction<SortByEnum | undefined>>;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  setSelectedFilterColumns: React.Dispatch<React.SetStateAction<FilterProto[]>>;
  setSelectedObservationColumns: React.Dispatch<React.SetStateAction<ObservationColumnProto[]>>;
  setBenchmarkMode: React.Dispatch<React.SetStateAction<"currentModel" | "targetModel">>;
  dateFilter: DateFilter | undefined;
  dateFilterCategory: DateFilterCategory | undefined;
  setDateFilter: React.Dispatch<React.SetStateAction<DateFilter | undefined>>;
  setDateFilterCategory: React.Dispatch<React.SetStateAction<DateFilterCategory>>;
  setSelectedMetricHeader: React.Dispatch<React.SetStateAction<Header | null>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const CreateMKG: React.FC<CreateMKG1Props> = ({
  metricInfo,
  targetModelInfo,
  modelInfo,
  allMetrics,
  measureColumn,
  allBenchmarks,
  allKGColumns,
  selectedBenchmark,
  handleAddSelectedBenchmark,
  handleAddRelatedAttributeEdge,
  handleRemoveRelatedAttribute,
  handleAddBenchmarkFilter,
  handleAddBenchmark,
  handleRemoveBenchmarkFilter,
  handleRemoveBenchmarkEdge,
  handleAddCorrelation,
  handleRemoveCorrelation,
  handleAddFundamentalEdges,
  handleRemoveFundamentalEdges,
  submitForm,
  benchmarkEdge,
  selectedRelatedAttributeColumn,
  setSelectedRelatedAttributeColumn,
  relatedAttributeEdge,
  relatedAttributeEdgeWeight,
  setRelatedAttributeEdgeWeight,
  benchmarkName,
  setBenchmarkName,
  benchmarkFilterValue,
  setBenchmarkFilterValue,
  benchmarkEdgeWeight,
  setBenchmarkEdgeWeight,
  fundamentalEdgeName,
  setFundamentalEdgeName,
  fundamentalEdgeWeight,
  setFundamentalEdgeWeight,
  selectedBenchmarkFilterColumn,
  setSelectedBenchmarkFilterColumn,
  selectedType,
  setSelectedType,
  setSelectedHeader,
  correlations,
  correlationFactor,
  setCorrelationFactor,
  benchmarkFilter,
  fundamentalEdges,
  isAdditive,
  setIsAdditive,
  isCorrelationMenuOpen,
  toggleCorrelationMenu,
  selectedColumnValues,
  handleToggle,
  columnNames,
  selectedObservationColumn,
  selectedFilterColumn,
  selectedAggregation,
  selectedOperator,
  inputValue,
  selectedFilterColumns,
  selectedObservationColumns,
  targetDataModelHeaders,
  selectedTargetModelId,
  isLoading,
  
  setSelectedObservationColumn,
  setSelectedFilterColumn,
  setSelectedColumnValues,
  setSelectedAggregation,
  setSelectedOperator,
  setInputValue,
  setSelectedFilterColumns,
  setSelectedObservationColumns,
  setColumnNames,
  handleTargetModelSelect,
  benchmarkMode,
  setBenchmarkMode,
  setDateFilter,
  setDateFilterCategory,
  dateFilter,
  dateFilterCategory,
  setSelectedMetricHeader,
  setIsLoading,
}) => {
  return (
    <Container maxW="container.xl">
      <VStack align="stretch" spacing={4}>
        <Box bg="white">
          <Button
            colorScheme="green"
            onClick={submitForm}
            w="100%"
            fontWeight="bold"
            fontSize="lg"
            leftIcon={<FaCheckCircle />}
          >
            Save
          </Button>
          </Box>
        <HStack spacing={4}>
          {/* Metric Details */}
          <Box
            flex="1"
            border="1px"
            borderColor="gray.300"
            p={3}
            borderRadius="lg"
            bg="gray.50"
            alignSelf="stretch"

          >
            <VStack align="stretch" spacing={2}>
              <Text fontSize="sm" fontWeight="bold" mb={0} w="100%">Metric Details</Text>
              <HStack>
                <Text fontWeight="bold" fontSize="sm" mb={0}>Name:</Text>
                <Text fontWeight="medium" fontSize="sm" mb={0}>{metricInfo?.metrics[0]?.header?.name || "N/A"}</Text>
              </HStack>
              <HStack>
                <Text fontWeight="bold" fontSize="sm" mb={0}>Description:</Text>
                <Text fontWeight="medium" fontSize="sm" mb={0}>{metricInfo?.metrics[0]?.header?.description || "N/A"}</Text>
              </HStack>
              <HStack>
                <Text fontWeight="bold" fontSize="sm" mb={0}>Model ID:</Text>
                <Text fontWeight="medium" fontSize="sm" color="fg.muted" mb={0}>
                  {metricInfo?.model_id || "N/A"}
                </Text>
              </HStack>
            </VStack>
          </Box>
          {/* Dimension relationships */}
          <Box
            flex="1"
            border="1px"
            borderColor="gray.300"
            p={3}
            borderRadius="lg"
            bg="gray.50"
            alignSelf="stretch"
            display="flex"
            flexDirection="column"
          >

            <VStack align="stretch" spacing={3} p={2}>
              <Text fontSize="sm" fontWeight="bold" mb={0} w="100%">Related Dimensions</Text>
              {relatedAttributeEdge &&
                relatedAttributeEdge.map((option, index) => (
                  <Box
                    key={index}
                    width="100%"
                    display="flex"
                    flexWrap="wrap"
                  >
                    <Tag
                      flexGrow={1}
                      bg="bg.surface"
                      borderWidth="1px"
                      borderRadius="lg"
                      borderColor="purple.700"
                      p={1}
                    >
                      <TagLabel flexGrow={1} textAlign="left" ml={1}>
                        <Text display="inline" color="fg.default" fontWeight={600} mb={0}> {option?.header?.name}</Text><Text display="inline" color="fg.muted" mb={0}> (wt: {option?.edge_weight})</Text>
                      </TagLabel>
                      <Button
                        variant="tertiary"
                        size="xs"
                        leftIcon={<DeleteIcon boxSize="0.8rem" />}
                        color="red.600"
                        onClick={() =>
                          handleRemoveRelatedAttribute(
                            option?.header?.id!
                          )
                        }
                      >
                        Clear
                      </Button>
                    </Tag>
                  </Box>
                ))}
              <HStack>
                <Select
                  value={selectedRelatedAttributeColumn?.id || ""}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    const selected =
                      allKGColumns &&
                      allKGColumns?.find((col) => col.id === selectedId);
                    setSelectedRelatedAttributeColumn(selected || null);
                  }}
                  bg="bg.surface"
                  fontSize="sm"
                >
                  <option value="">Select Dimension</option>
                  {allKGColumns &&
                    allKGColumns
                      .filter((column) =>
                        relatedAttributeEdge
                          ? !relatedAttributeEdge.find(
                            (option) => option.header?.id === column.id
                          )
                          : []
                      )
                      .map((column) => (
                        <option key={column.id} value={column.id}>
                          {column.name}
                        </option>
                      ))}
                </Select>
                <Input
                  type="number"
                  placeholder="Weight (0-1)"
                  value={relatedAttributeEdgeWeight}
                  onChange={(e) =>
                    setRelatedAttributeEdgeWeight(parseFloat(e.target.value))
                  }
                  fontSize="sm"
                />
              </HStack>

            </VStack>
            <Flex gap={4} w="100%" mt="auto" p={2}>
              <Button
                onClick={handleAddRelatedAttributeEdge}
                colorScheme="purple"
                leftIcon={<AddIcon boxSize="0.9rem" />}
                fontWeight="bold"
                w="100%"
              >
                Add Dimension Edge
              </Button>
            </Flex>
          </Box>
        </HStack>
        <HStack spacing={4}>
          {/* Benchmark relationships */}
          <Box
            flex="1"
            border="1px"
            borderColor="gray.300"
            p={3}
            borderRadius="lg"
            bg="gray.50"
            alignSelf="stretch"
            display="flex"
            flexDirection="column"
          >
             <VStack alignSelf="stretch" align="stretch" spacing={3} p={2}>
             <Flex justifyContent="space-between" alignItems="center" mb={1}>
                <Text fontSize="sm" fontWeight="bold" w="auto" mb={0}>Related Benchmarks</Text>
                <HStack>
                  <Select
                    placeholder="Select benchmark"
                    value={selectedBenchmark?.id || ""}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      handleAddSelectedBenchmark(selectedId);
                    }}
                    size="sm"
                    maxW="200px"
                  >
                    {allBenchmarks?.map(benchmark => (
                      <option key={benchmark.id} value={benchmark.id}>
                        {benchmark.name}
                      </option>
                    ))}
                  </Select>
                </HStack>
              </Flex>
                {benchmarkEdge && benchmarkEdge.map((benchmarkForm, index) => (
                  <HStack
                    flexGrow={1}
                    bg="bg.surface"
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor="purple.700"
                    p={1}
                    mb={3}
                  >
                    <Box flexGrow={1} textAlign="left" m={1} whiteSpace="normal" wordBreak="break-word">
                      <Text fontWeight={700} fontSize="sm" mb={0} color="fg.muted">{benchmarkForm.benchmark?.header?.name ?? benchmarkForm?.header?.name}
                        <Text display="inline" color="fg.muted" mb={0} fontWeight="normal" fontSize="sm"> (wt: {benchmarkForm.edge_weight})</Text>
                      </Text>
                      {benchmarkForm?.benchmark?.definition?.observation_columns && (
                        <>
                          {/* Observation Columns */}
                          <Box mb={0}>
                            {benchmarkForm.benchmark.definition.observation_columns.map((filter, index) => (
                              <Badge bg="purple.50" size="xs" fontWeight={600} mr={2} key={`observation-${index}`}>
                                <Text display="inline" color="fg.default" mb={0}>
                                  {filter?.logical_column_header?.name}
                                </Text>{" "}
                                <Text display="inline" color="fg.default" mb={0}>
                                  {aggregationTypeToJSON(filter.aggregation!)} {sortByEnumToJSON(filter.sort_by!)}
                                </Text>
                              </Badge>
                            ))}
                          </Box>
                          </>
                        )}

                      {benchmarkForm?.benchmark?.definition?.filters && (
                        <>
                          {/* Filters */}
                          <Box>
                            {benchmarkForm.benchmark.definition.filters.map((filter, index) => (
                              <Badge bg="purple.50" size="xs" fontWeight={600} mr={2} key={`filter-${index}`}>
                                <Text display="inline" color="fg.default" mb={0}>
                                  {filter?.logical_column_header?.name}
                                </Text>{" "}
                                <Text display="inline" color="fg.default" mb={0}>
                                  {filterOperatorToJSON(filter.operator!)} {filter.date_filter ? getDateFilterName(filter.date_filter): filter.values?.join(", ")}
                                </Text>
                              </Badge>
                            ))}
                          </Box>
                        </>
                      )}
                      {benchmarkForm?.benchmark?.benchmark_filters?.map((filter, filterIndex) => (
                        <Badge key={filterIndex} bg="purple.50" size="xs" fontWeight={600}>
                          {filter?.logical_column_header?.name}{" "}
                          {filterOperatorToJSON(filter.operator!)} {filter.date_filter ? getDateFilterName(filter.date_filter): filter.values?.join(", ")}
                        </Badge>
                      ))}
                    </Box>
                    <Button
                      variant="tertiary"
                      size="xs"
                      leftIcon={<DeleteIcon boxSize="0.8rem" />}
                      color="red.600"
                      onClick={() => handleRemoveBenchmarkEdge(index)}
                    >
                      Clear
                    </Button>
                  </HStack>

                ))}
                {/* Benchmark Creation Mode Switch */}
              <RadioGroup 
                onChange={(val) => setBenchmarkMode(val as "currentModel" | "targetModel")} 
                value={benchmarkMode}
                colorScheme="purple"
              >
                <HStack spacing="24px">
                  <Radio value="currentModel">Current Model</Radio>
                  <Radio value="targetModel">Target Model</Radio>
                </HStack>
              </RadioGroup>
              <HStack spacing={4} width="100%">
                <FormControl>
                  <FormLabel fontSize="xs" fontWeight="bold">Edge Name</FormLabel>
                  <Input
                    type="text"
                    placeholder="Edge Name"
                    value={benchmarkName}
                    onChange={(e) => setBenchmarkName(e.target.value)}
                    fontSize="sm" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" fontWeight="bold"> Edge Weight</FormLabel>
                  <Input
                    type="number"
                    placeholder="Edge Weight"
                    value={benchmarkEdgeWeight}
                    onChange={(e) => setBenchmarkEdgeWeight(parseFloat(e.target.value))}
                    step="0.1"
                    fontSize="sm" />
                </FormControl>
              </HStack>
            </VStack>
            {benchmarkMode === "currentModel" ? (
              <VStack alignSelf="stretch" align="stretch" spacing={3} p={2}>
                <VStack bg="purple.50" spacing={3} borderRadius="lg" align="stretch" p={2} borderWidth="1px" borderColor="purple.700">
                    {benchmarkFilter && benchmarkFilter.length > 0 && (
                      <VStack spacing={4}>
                        {benchmarkFilter && benchmarkFilter.map(filter => (
                          <Flex key={filter?.logical_column_header?.id} align="center" gap={3} borderColor="purple.700" borderRadius="lg" borderWidth="1px" fontSize="sm" w="100%" justifyContent="space-between" bg="bg.surface">
                            <Box ml={2}>
                              <Text display="inline" mb={0} fontWeight="medium" fontSize="xs">{filter?.logical_column_header?.name}{filter?.kg_column_header?.name ? `${filter?.kg_column_header?.name}` : ""}</Text> {filterOperatorToJSON(filter.operator!)} <Text display="inline" mb={0} fontWeight="medium">{filter.values?.join(", ")}</Text>
                            </Box>
                            <Button
                              variant="tertiary"
                              size="xs"
                              leftIcon={<DeleteIcon boxSize="0.8rem" />}
                              color="red.600"
                              onClick={() => handleRemoveBenchmarkFilter(filter.logical_column_header?.id!)}
                            >
                            </Button>
                          </Flex>
                        ))}
                      </VStack>
                    )}
                    <HStack spacing={4}>
                      <FormControl>
                        <Select
                          value={selectedBenchmarkFilterColumn?.header?.id || ""}
                          onChange={(e) => {
                            const selectedId = e.target.value;
                            const selected = modelInfo?.columns?.find(col => col.header?.id === selectedId);
                            setSelectedBenchmarkFilterColumn(selected || null);
                          } }
                          bg="bg.surface"
                          fontSize="xs"
                        >
                          <option value="">Filter Column</option>
                          {modelInfo?.columns?.map((column, index) => (
                            <option key={column.header?.id} value={column.header?.id || ""}>
                              {column.header?.name}
                            </option>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl fontSize="xs" position="relative" zIndex={10}>
                        {columnNames && selectedBenchmarkFilterColumn?.column_type === ColumnType.ATTRIBUTE ? (
                          <Menu closeOnSelect={false} preventOverflow={true} placement="top-end">
                            <MenuButton
                              as={Button}
                              color="fg.default"
                              bg="bg.surface"
                              maxW="100%"
                              width="100%"
                              rightIcon={<ChevronDownIcon mr={-2} />}
                              fontSize="sm"
                              border="1px"
                              borderColor="gray.300"
                              borderRadius="md"
                              fontWeight="normal"
                              _hover={{ bg: "purple.50" }}
                              textAlign="left"
                            >
                              {selectedColumnValues.length > 0
                                ? `Selected (${selectedColumnValues.length})`
                                : "Select Options"}
                            </MenuButton>
                            <Portal>
                              <MenuList maxHeight="200px" overflowY="auto" zIndex={1500} position="absolute">
                                {columnNames.map((option, index) => (
                                  <MenuItem key={index} minH="40px" zIndex={10}>
                                    <Checkbox
                                      isChecked={selectedColumnValues.includes(option)}
                                      onChange={() => handleToggle(option)}
                                      colorScheme="purple"
                                    >
                                      {option}
                                    </Checkbox>
                                  </MenuItem>
                                ))}
                              </MenuList>
                            </Portal>
                          </Menu>
                        ) : (
                          <Input
                            type="text"
                            placeholder="Filter Value"
                            value={benchmarkFilterValue}
                            onChange={(e) => setBenchmarkFilterValue(e.target.value)}
                            fontSize="xs" />
                        )}
                      </FormControl>

                      <Button
                        borderRadius="full"
                        variant="solid"
                        bg="purple.700"
                        leftIcon={<AddIcon />}
                        size="xs"
                        iconSpacing={0}
                        onClick={handleAddBenchmarkFilter}
                      ></Button>
                    </HStack>
                  </VStack>
              </VStack>
            ):(
              <VStack spacing={4} align="stretch" width="full">
                {/* Model Selection - Always visible */}
                <FormControl width="97%" ml={2}>
                  <FormLabel fontSize="xs" fontWeight="bold">Target Model</FormLabel>
                  <Select
                    placeholder="Select a Target Model"
                    value={selectedTargetModelId}
                    onChange={(e) => handleTargetModelSelect(e.target.value)}
                    fontSize="sm"
                    bg="bg.surface"
                  >
                    {targetDataModelHeaders?.map((model: Header) => (
                      <option key={model.id} value={model.id}>
                        {model.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                {selectedTargetModelId && (
                  <HStack spacing={4} align="start">
                    {/* Observation Section */}
                    <ObservationComponent
                      type="observation"
                      columns={targetModelInfo?.columns!}
                      selectedObservationColumn={selectedObservationColumn}
                      selectedFilterColumn={selectedFilterColumn}
                      selectedAggregation={selectedAggregation}
                      selectedOperator={selectedOperator}
                      inputValue={inputValue}
                      selectedColumnValues={selectedColumnValues}
                      selectedFilterColumns={selectedFilterColumns}
                      selectedObservationColumns={selectedObservationColumns}
                      dateFilter={dateFilter}
                      dateFilterCategory={dateFilterCategory}
                      isLoading={isLoading}
                      setSelectedObservationColumn={setSelectedObservationColumn}
                      setColumnNames={setColumnNames}
                      setSelectedFilterColumn={setSelectedFilterColumn}
                      setSelectedColumnValues={setSelectedColumnValues}
                      setSelectedAggregation={setSelectedAggregation}
                      setSelectedOperator={setSelectedOperator}
                      setInputValue={setInputValue}
                      setSelectedFilterColumns={setSelectedFilterColumns}
                      setSelectedObservationColumns={setSelectedObservationColumns}
                      setDateFilter={setDateFilter}
                      setDateFilterCategory={setDateFilterCategory}
                      setIsLoading={setIsLoading}
                    />
                    {/* Filters Section */}
                    <ObservationComponent
                      type="filter"
                      columns={targetModelInfo?.columns!}
                      columnNames={columnNames}
                      selectedObservationColumn={selectedObservationColumn}
                      selectedFilterColumn={selectedFilterColumn}
                      selectedAggregation={selectedAggregation}
                      selectedOperator={selectedOperator}
                      inputValue={inputValue}
                      selectedColumnValues={selectedColumnValues}
                      selectedFilterColumns={selectedFilterColumns}
                      selectedObservationColumns={selectedObservationColumns}
                      dateFilter={dateFilter}
                      dateFilterCategory={dateFilterCategory}
                      isLoading={isLoading}
                      setSelectedObservationColumn={setSelectedObservationColumn}
                      setColumnNames={setColumnNames}
                      setSelectedFilterColumn={setSelectedFilterColumn}
                      setSelectedColumnValues={setSelectedColumnValues}
                      setSelectedAggregation={setSelectedAggregation}
                      setSelectedOperator={setSelectedOperator}
                      setInputValue={setInputValue}
                      setSelectedFilterColumns={setSelectedFilterColumns}
                      setSelectedObservationColumns={setSelectedObservationColumns}
                      setDateFilter={setDateFilter}
                      setDateFilterCategory={setDateFilterCategory}
                      setIsLoading={setIsLoading}
                    />
                  </HStack>
                )}
              </VStack>
            )}
            
            <Flex w="100%" mt="auto" p={2}>
              <Button
                onClick={handleAddBenchmark}
                w="100%"
                colorScheme="purple"
                leftIcon={<AddIcon boxSize="0.9rem" />}
                fontWeight="bold"
              >
                Add Benchmark Edge
              </Button>
            </Flex>   
          </Box>
          {/* fundamental relationships */}
          <Box
            flex="1"
            border="1px"
            borderColor="gray.300"
            p={3}
            borderRadius="lg"
            bg="gray.50"
            alignSelf="stretch"
            display="flex"
            flexDirection="column"
          >
            <VStack align="stretch" spacing={3} p={2}>
              <Text fontSize="sm" fontWeight="bold" w="100%" mb={0}>Related Formulae</Text>
              {fundamentalEdges &&
                fundamentalEdges.map((fundamentalEdge, index) => (
                  <HStack
                    key={index}
                    flexGrow={1}
                    bg="bg.surface"
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor="purple.700"
                    p={1}
                    mb={3}
                  >
                    <Box flexGrow={1} textAlign="left" m={1} whiteSpace="normal" wordBreak="break-word">
                      <Text fontWeight={700} fontSize="sm" mb={0} color="fg.muted">
                        {fundamentalEdge.header?.name}
                        <Text display="inline" color="fg.muted" mb={0} fontWeight="normal" fontSize="sm">
                          {" "}(wt: {fundamentalEdge.edge_weight})
                        </Text>
                      </Text>
                      {fundamentalEdge.fundamental_relationship &&
                        fundamentalEdge.fundamental_relationship.correlations.map(
                          (correlation, correlationIndex) => (
                            <Badge bg="purple.50" size="xs" fontWeight={500} key={correlationIndex} mr={1}>
                              <Text display="inline" color="fg.default" mb={0}>
                                {correlation.related_column_header?.name || correlation.related_metric_header?.name}
                              </Text>
                              <Text display="inline" color="fg.default" mb={0}>
                                {" (CF: "}{correlation.correlation_factor}{") "}
                              </Text>
                            </Badge>
                          )
                        )}

                    </Box>
                    <Button
                      variant="tertiary"
                      size="xs"
                      leftIcon={<DeleteIcon boxSize="0.8rem" />}
                      color="red.600"
                      onClick={() => handleRemoveFundamentalEdges(index)}
                    >
                      Clear
                    </Button>
                  </HStack>
                ))}

              <HStack spacing={4}>
                <FormControl>
                  <FormLabel fontSize="xs" fontWeight="bold">Edge Name</FormLabel>
                  <Input
                    type="text"
                    placeholder="Edge Name"
                    value={fundamentalEdgeName}
                    onChange={(e) => setFundamentalEdgeName(e.target.value)}
                    fontSize="sm"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" fontWeight="bold">Edge Weight</FormLabel>
                  <Input
                    type="number"
                    placeholder="Edge Weight"
                    value={fundamentalEdgeWeight}
                    onChange={(e) => setFundamentalEdgeWeight(parseFloat(e.target.value))}
                    step="0.001"
                    fontSize="sm"
                  />
                </FormControl>
                <Box alignSelf="flex-end">
                  <Button
                    colorScheme="purple"
                    variant="outline"
                    onClick={toggleCorrelationMenu}
                    leftIcon={isCorrelationMenuOpen ? <TbCalculatorOff /> : <TbCalculator />}
                    iconSpacing={0}
                  />
                </Box>
              </HStack>

              {/* Dropdown for selecting metrics */}
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="bold">Related Metric</FormLabel>
                <Select
                  placeholder="Select a metric"
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    const selectedMetric = allMetrics?.find(metric => metric.id === selectedId) || null;
                    setSelectedMetricHeader(selectedMetric);
                  }}
                  fontSize="sm"
                  bg="bg.surface"
                >
                  {allMetrics?.map((metric) => (
                    <option key={metric.id} value={metric.id}>
                      {metric.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl display="flex" alignItems="center">
                <FormLabel fontSize="xs" fontWeight="bold">Is Additive:</FormLabel>
                  <Checkbox
                    mt={1}
                    isChecked={isAdditive}
                    onChange={() => setIsAdditive(!isAdditive)}
                    colorScheme="purple"
                  />
                </FormControl>
              <Collapse in={isCorrelationMenuOpen} animateOpacity>
                <VStack bg="purple.50" spacing={3} borderRadius="lg" align="stretch" p={2} borderWidth="1px" borderColor="purple.700">
                  {correlations && correlations.length > 0 && (
                    <VStack spacing={3}>
                      {correlations && correlations.map((correlation, index) => (
                        <Flex key={index} align="center" gap={3} borderColor="purple.700" borderRadius="lg" borderWidth="1px" fontSize="sm" w="100%" justifyContent="space-between" bg="bg.surface">
                          <Box ml={2}>
                            <Text display="inline" mb={0} fontWeight="medium" fontSize="xs">
                              {correlation.related_column_header?.name || correlation.related_metric_header?.name} {"(CF: "} {correlation.correlation_factor}{")"}
                            </Text>
                          </Box>
                          <Button
                            variant="tertiary"
                            size="xs"
                            leftIcon={<DeleteIcon boxSize="0.8rem" />}
                            color="red.600"
                            onClick={() => handleRemoveCorrelation(index)}
                          >
                          </Button>
                        </Flex>
                      ))}
                    </VStack>
                  )}
                  <HStack spacing={4}>
                    <FormControl>
                      <Select
                        value={selectedType}
                        onChange={(e) => setSelectedType(e.target.value)}
                        bg="bg.surface"
                        fontSize="xs"
                      >
                        <option value="Column">Column</option>
                        <option value="Metric">Metric</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <Select
                        onChange={(e) => setSelectedHeader(e.target.value)}
                        bg="bg.surface"
                        fontSize="xs"
                      >
                        <option value="">Select Header</option>
                        {(selectedType === "Column" ? measureColumn : allMetrics)?.map((header, index) => (
                          <option key={index} value={header.name}>
                            {header.name}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fontSize="sm">
                      <Input
                        type="number"
                        placeholder="Correlation Factor"
                        value={correlationFactor}
                        onChange={(e) => setCorrelationFactor(e.target.value)}
                        step="0.001"
                        fontSize="xs"
                      />
                    </FormControl>
                    <Button
                      borderRadius="full"
                      variant="solid"
                      bg="purple.700"
                      leftIcon={<AddIcon />}
                      size="xs"
                      iconSpacing={0}
                      onClick={handleAddCorrelation}
                    ></Button>
                  </HStack>
                </VStack>
              </Collapse>
            </VStack>


            <Flex mt="auto" w="100%" p={2}>
              <Button
                onClick={handleAddFundamentalEdges}
                w="100%"
                colorScheme="purple"
                leftIcon={<AddIcon boxSize="0.9rem" />}
                fontWeight="bold"
              >
                Add Fundamental Edge
              </Button>
            </Flex>

          </Box>
        </HStack>
      </VStack>
    </Container>
  );
};
export default CreateMKG;